import {
  planGuestRooms,
  describeBunkMix,
  type SleepingPreference,
} from "./guest-room-plan";
import type { QuickAnswers } from "./BlissQuickStart";
import type { BunkSelection } from "./BunkOptions";
import {
  MAX_CLASSIC_GUESTS,
  recommendClassicConfiguration,
} from "./classic-recommendation";
export { MAX_CLASSIC_GUESTS } from "./classic-recommendation";
import { photoPurchaseSnapshot } from "./photo/photo-purchase-snapshot";

export const JOURNEY_STEPS = [
  "Your people",
  "Their comfort",
  "What we heard",
  "Our recommendation",
  "Explore",
  "Your details",
  "Investment & next steps",
] as const;
export const AGE_GROUPS = [
  "Babies & Infants",
  "Toddlers & Tweens",
  "Teens & Adult Kids",
  "Parents & Grandparents",
] as const;
export type AgeGroup = (typeof AGE_GROUPS)[number];
export type JourneyDirection = "classic" | "custom" | "one_of_a_kind";
export type JourneyIntent =
  | "designer"
  | "deposit"
  | "room_review"
  | "monthly_plan"
  | "design_only"
  | "save";
export type ConsultativeAnswers = {
  entryMode?: "transformation" | "consultation";
  groups: AgeGroup[];
  purpose: "family" | "rental" | "both";
  sleepers: number | null;
  sharing: SleepingPreference;
  guestBuild: "undecided" | "slender" | "average" | "broader" | "mixed";
  soloGuests: number;
  dedicatedRooms: number;
  mixedRooms: number;
  legroom: "" | "standard" | "longer" | "undecided";
  access: "" | "stairs" | "ladder" | "lower_bed" | "undecided";
  direction: JourneyDirection;
  inspirationId?: string;
  timing: string;
  contactPreference?: "email" | "phone" | "both";
  installationMonth: string;
  paymentFrequency: "monthly" | "biweekly";
  planMonths: 3 | 6 | 9 | 12;
  upfrontAmount: number;
  intent?: JourneyIntent;
  confirmed: boolean;
};
export const EMPTY_CONSULTATION: ConsultativeAnswers = {
  groups: [],
  purpose: "family",
  sleepers: null,
  sharing: "undecided",
  guestBuild: "undecided",
  soloGuests: 0,
  dedicatedRooms: 1,
  mixedRooms: 0,
  legroom: "",
  access: "",
  direction: "classic",
  timing: "",
  contactPreference: "email",
  installationMonth: "",
  paymentFrequency: "monthly",
  planMonths: 12,
  upfrontAmount: 0,
  confirmed: false,
};
/** Starting suggestion only; customers can choose another payment term. */
export function suggestedPlanMonths(timing: string = ""): 3 | 6 | 9 | 12 {
  if (/asap|as soon|1[–-]3/i.test(timing)) return 3;
  if (/3[–-]6|flexible|up to 6/i.test(timing)) return 6;
  if (/plenty of time|up to 9/i.test(timing)) return 9;
  if (/later this year/i.test(timing)) {
    const monthsLeft = 12 - new Date().getMonth();
    return monthsLeft <= 3 ? 3 : monthsLeft <= 6 ? 6 : monthsLeft <= 9 ? 9 : 12;
  }
  return 12;
}
export const CONSULTATIVE_KEY = "bliss_consultative_journey_v1";
export function normalizeConsultation(value: unknown): ConsultativeAnswers {
  const v =
    value && typeof value === "object"
      ? (value as Partial<ConsultativeAnswers>)
      : {};
  return {
    ...EMPTY_CONSULTATION,
    entryMode: v.entryMode === "transformation" ? "transformation" : "consultation",
    groups: AGE_GROUPS.filter(
      (g) => Array.isArray(v.groups) && v.groups.includes(g),
    ),
    purpose:
      v.purpose === "rental" || v.purpose === "both" ? v.purpose : "family",
    sleepers:
      Number.isInteger(v.sleepers) &&
      v.sleepers! >= 1 &&
      v.sleepers! <= MAX_CLASSIC_GUESTS
        ? v.sleepers!
        : null,
    guestBuild: ["slender", "average", "broader", "mixed"].includes(
      v.guestBuild ?? "",
    )
      ? v.guestBuild!
      : "undecided",
    sharing: ["shared", "separate", "mixed"].includes(v.sharing ?? "")
      ? v.sharing!
      : "undecided",
    soloGuests: Number.isInteger(v.soloGuests)
      ? Math.max(0, Math.min(v.sleepers ?? 100, v.soloGuests!))
      : 0,
    dedicatedRooms: Number.isInteger(v.dedicatedRooms)
      ? Math.max(0, Math.min(100, v.dedicatedRooms!))
      : 1,
    mixedRooms: Number.isInteger(v.mixedRooms)
      ? Math.max(0, Math.min(100, v.mixedRooms!))
      : 0,
    legroom: ["standard", "longer", "undecided"].includes(v.legroom ?? "")
      ? v.legroom!
      : "",
    access: ["stairs", "ladder", "lower_bed", "undecided"].includes(
      v.access ?? "",
    )
      ? v.access!
      : "",
    direction:
      v.direction === "custom" || v.direction === "one_of_a_kind"
        ? v.direction
        : "classic",
    inspirationId:
      typeof v.inspirationId === "string"
        ? v.inspirationId.slice(0, 80)
        : undefined,
    timing: typeof v.timing === "string" ? v.timing.slice(0, 120) : "",
    contactPreference: v.contactPreference === "phone" || v.contactPreference === "both" ? v.contactPreference : "email",
    planMonths: v.planMonths === 3 || v.planMonths === 6 || v.planMonths === 9 || v.planMonths === 12 ? v.planMonths : suggestedPlanMonths(v.timing),
    upfrontAmount:
      typeof v.upfrontAmount === "number" &&
      Number.isFinite(v.upfrontAmount) &&
      v.upfrontAmount >= 0
        ? Math.round(v.upfrontAmount * 100) / 100
        : 0,
    paymentFrequency:
      v.paymentFrequency === "biweekly" ? "biweekly" : "monthly",
    installationMonth:
      typeof v.installationMonth === "string" &&
      /^20\d{2}-(0[1-9]|1[0-2])$/.test(v.installationMonth)
        ? v.installationMonth
        : "",
    intent: [
      "designer",
      "deposit",
      "room_review",
      "design_only",
      "monthly_plan",
      "save",
    ].includes(v.intent ?? "")
      ? v.intent
      : undefined,
    confirmed: v.confirmed === true,
  };
}
export const infantsOnly = (a: ConsultativeAnswers) =>
  a.groups.length === 1 && a.groups[0] === "Babies & Infants";
export const mixedGenerations = (a: ConsultativeAnswers) =>
  a.groups.includes("Parents & Grandparents") && a.groups.length > 1;
export function stageReady(step: number, a: ConsultativeAnswers) {
  if (step === 1) return a.groups.length > 0;
  if (step === 2)
    return (
      a.dedicatedRooms + a.mixedRooms > 0 &&
      a.dedicatedRooms + a.mixedRooms <= 100 &&
      (infantsOnly(a) || (!!a.legroom && !!a.access))
    );
  if (step === 3) return a.confirmed;
  // Contact persistence is checked by the journey before entering stage seven.
  if (step === 6) return true;
  return step >= 1 && step <= 7;
}
export function consultationRoomPlan(a: ConsultativeAnswers) {
  return planGuestRooms(
    a.sleepers ?? 2,
    a.sharing,
    a.soloGuests,
    a.dedicatedRooms,
    a.mixedRooms,
    a.legroom === "longer",
    a.guestBuild === "broader",
  );
}
export function consultationRecommendation(a: ConsultativeAnswers) {
  const plan = consultationRoomPlan(a);
  const recommendation = recommendClassicConfiguration({
    choices: plan.choices,
    sleepers: Math.max(2, a.sleepers ?? 2),
    exactSleeperCount: true,
    preferences: {
      access: a.access || "undecided",
      legroom: a.legroom || "undecided",
      separateMattresses: a.sharing === "separate",
    },
  })!;
  recommendation.capacity = plan.capacity;
  recommendation.sharingRequired = plan.sharingGuests > 0;
  const manyRooms = a.dedicatedRooms + a.mixedRooms > 1;
  if (manyRooms) {
    recommendation.arrangement = "multiple";
    recommendation.title = `${plan.choices.length} Classic ${plan.choices.length === 1 ? "bunk" : "bunks"} across your rooms`;
  }
  recommendation.explanation = `${a.sharing === "undecided" ? "If queen mattresses can be shared, " : "Based on your sleeping preferences, "}${describeBunkMix(plan.choices)} can provide ${plan.capacity} sleeping places${a.sleepers ? ` for your ${a.sleepers} ${a.sleepers === 1 ? "guest" : "guests"}` : "; we’ll confirm your guest count"}. ${a.sharing === "separate" ? "Everyone has their own mattress." : plan.sharingGuests ? `This arrangement needs ${plan.sharingGuests} guests to share queen mattresses, two per shared mattress.` : "This guest count can be accommodated without sharing mattresses."}`;
  if (a.guestBuild === "broader")
    recommendation.explanation +=
      " We’ve suggested queen-width mattresses to give broader-framed guests more individual space. Sharing remains your choice; two people per queen may not feel comfortable for every pair.";
  if (a.guestBuild === "mixed")
    recommendation.explanation +=
      " With different guest builds, your designer can tailor mattress widths to the individual sleepers.";
  if (a.sharing === "mixed")
    recommendation.explanation += ` At least ${a.soloGuests} guests retain their own mattress.`;
  if (a.access === "lower_bed")
    recommendation.explanation +=
      " Explore a perpendicular lower-bunk layout, with room to approach each lower bed from both sides.";
  if (mixedGenerations(a))
    recommendation.explanation +=
      " Your designer will confirm mattress sizes and access for each person.";
  if (a.mixedRooms)
    recommendation.explanation +=
      " Mixed-use rooms need space for their daytime purpose as well as sleeping.";
  recommendation.explanation +=
    " This is a capacity starting point; room measurements determine what fits.";
  return recommendation;
}
export function consultationBunks(a: ConsultativeAnswers): BunkSelection[] {
  return consultationRoomPlan(a).rooms.flatMap((room) =>
    room.choices.map((choice, index) => ({
      choice,
      finish: "gloss_white" as const,
      upgrades:
        a.access === "stairs" && (room.choices.length !== 2 || index === 0)
          ? { stairs: true }
          : {},
    })),
  );
}
export function consultationRoomSnapshots(
  a: ConsultativeAnswers,
  bunks = consultationBunks(a),
) {
  let offset = 0;
  return consultationRoomPlan(a)
    .rooms.filter((room) => room.choices.length)
    .map((room) => {
      const selections = bunks.slice(offset, offset + room.choices.length);
      offset += room.choices.length;
      return {
        ...room,
        configuratorSnapshot: consultationSnapshot(a, selections),
      };
    });
}
export function consultationSnapshot(
  a: ConsultativeAnswers,
  bunks = consultationBunks(a),
) {
  const snapshot = photoPurchaseSnapshot(
    bunks.map((b) => b.choice),
    bunks.map((b) => b.upgrades),
    bunks.map((b) => b.finish),
    bunks.map((b) => b.customColorCode),
  );
  // A shared central staircase is one proposed item, not two staircase charges.
  // It remains subject to a completed layout quote before checkout.
  if (bunks.length === 2 && a.access === "stairs") {
    snapshot.beds = snapshot.beds.map((b) =>
      b.kind === "stairs"
        ? { ...b, servesBedIds: ["photo-bunk-0", "photo-bunk-1"] }
        : { ...b, ladder: false },
    );
  }
  return snapshot;
}
export function needsLayoutReview(a: ConsultativeAnswers) {
  return (
    !a.sleepers ||
    a.sleepers > 6 ||
    infantsOnly(a) ||
    a.groups.includes("Toddlers & Tweens") ||
    a.groups.includes("Babies & Infants") ||
    a.access === "lower_bed" ||
    a.access === "undecided" ||
    a.legroom === "undecided" ||
    consultationRecommendation(a).bunkCount > 1
  );
}
export function consultationQuickAnswers(
  a: ConsultativeAnswers,
  bunks = consultationBunks(a),
): QuickAnswers {
  return {
    audience: a.groups.join(", "),
    roomPurpose: a.purpose,
    sleepers: a.sleepers ?? 2,
    choice: bunks[0]?.choice ?? 0,
    choices: bunks.map((b) => b.choice),
    bunks,
    timing: a.timing,
    productLine: a.direction === "classic" ? "basic" : "custom",
    classicPreferences: {
      access: a.access || "undecided",
      legroom: a.legroom || "undecided",
      separateMattresses: a.sharing === "separate",
    },
    classicRecommendation: consultationRecommendation(a),
    consultation: { ...a },
    customIdea:
      a.direction === "classic"
        ? undefined
        : `Interested in ${a.direction === "custom" ? "the custom portfolio" : "one-of-a-kind concepts"}${a.inspirationId ? `: ${a.inspirationId}` : ""}.`,
  };
}
export function repeatBackSummary(a: ConsultativeAnswers) {
  const list = new Intl.ListFormat("en", { style: "long", type: "conjunction" });
  const count = a.sleepers
    ? `${a.sleepers} ${a.sleepers === 1 ? "guest" : "guests"}`
    : "Guest count to be decided";
  const people = a.groups.length === AGE_GROUPS.length
    ? "spanning babies through grandparents"
    : list.format(a.groups.map((group) => group.toLowerCase()));
  const rooms = [
    a.dedicatedRooms > 0 && `${a.dedicatedRooms} dedicated bunk ${a.dedicatedRooms === 1 ? "room" : "rooms"}`,
    a.mixedRooms > 0 && `${a.mixedRooms} mixed-use ${a.mixedRooms === 1 ? "room" : "rooms"}`,
  ].filter(Boolean) as string[];
  const rows = [
    { label: "Your guests", detail: `${count}${people ? `, ${people}` : ""}.` },
    {
      label: a.dedicatedRooms + a.mixedRooms === 1 ? "Your room" : "Your rooms",
      detail: `${rooms.length ? list.format(rooms) : "Room count to be decided"}.${mixedGenerations(a) ? (a.dedicatedRooms + a.mixedRooms === 1 ? " Adults and children will stay together." : " Planning for adults and children.") : ""}`,
    },
  ];
  if (infantsOnly(a)) {
    rows.push({ label: "Sleep planning", detail: "A separate infant sleep space, with Classic options for the years ahead." });
    return rows;
  }
  if (a.sleepers !== 1) {
    const solo = Math.min(a.soloGuests, a.sleepers ?? a.soloGuests);
    rows.push({
      label: "Sleeping arrangements",
      detail: a.sharing === "separate"
        ? "Everyone needs their own mattress."
        : a.sharing === "shared"
          ? "Queen beds can be shared."
          : a.sharing === "mixed"
            ? solo === 0
              ? "Shared queen beds and individual mattresses; the mix is still open."
              : `${solo} ${solo === 1 ? "guest needs" : "guests need"} their own mattress${a.sleepers !== null && solo === a.sleepers ? "." : "; others may share queen beds."}`
            : "Explore shared queen beds and individual mattresses.",
    });
  }
  const comfort = [
    a.legroom === "longer" ? "Extra legroom" : a.legroom === "standard" ? "Standard mattress lengths" : "Help choosing mattress lengths",
    a.access === "stairs" ? "stair access" : a.access === "ladder" ? "ladder access" : a.access === "lower_bed" ? "Easy Access Lower Bunks — perpendicular, with room to approach both sides" : "guidance on access",
  ];
  const build = {
    broader: "More sleeping width for broader-framed guests.",
    mixed: "A mix of builds and sleeping-space needs.",
    slender: "Mostly slender guests.",
    average: "Mostly average-build guests.",
    undecided: "",
  }[a.guestBuild];
  rows.push({ label: "Comfort preferences", detail: `${list.format(comfort)}.${build ? ` ${build}` : ""}` });
  return rows;
}

export function repeatBack(a: ConsultativeAnswers) {
  const count = a.sleepers
    ? `${a.sleepers} ${a.sleepers === 1 ? "guest" : "guests"}`
    : "a guest count we’ll work out together";
  const access = {
    stairs: "stair access",
    ladder: "ladder access",
    lower_bed:
      "Easy Access Lower Bunks in a perpendicular layout, with room to access each lower bed from both sides",
    undecided: "guidance on access",
    "": "guidance on access",
  }[a.access];
  return `You’re making room for ${count}, including ${a.groups.join(" and ").toLowerCase()}.${mixedGenerations(a) ? (a.dedicatedRooms + a.mixedRooms > 1 ? " Adults and children are included in your room plan." : " Adults and children will be sharing the same bunk room.") : ""} ${a.guestBuild === "broader" ? "You’d like more sleeping width for broader-framed guests." : a.guestBuild === "mixed" ? "Your guests have a mix of builds and sleeping-space needs." : a.guestBuild === "slender" ? "Your guests are mostly slender." : a.guestBuild === "average" ? "Your guests are mostly of average build." : ""} You have ${a.dedicatedRooms} dedicated bunk ${a.dedicatedRooms === 1 ? "room" : "rooms"} and ${a.mixedRooms} mixed-use ${a.mixedRooms === 1 ? "room" : "rooms"} available. ${a.sharing === "separate" ? "Everyone needs their own mattress." : a.sharing === "shared" ? "Queen mattresses can be shared." : a.sharing === "mixed" ? `${a.soloGuests} guests need their own mattress; others may share queens.` : "We’ll compare shared queen beds with individual sleeping places."}${infantsOnly(a) ? " We’ll plan for a separate infant sleep space and consider Classic for the years ahead." : ` You’d like ${a.legroom === "longer" ? "extra legroom" : a.legroom === "standard" ? "standard mattress lengths" : "help choosing mattress lengths"} and ${access}.`}`;
}
